import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useToast } from '@/components/ui/use-toast'
import { delUrlFetcher, putFetcher } from 'api/swr'
import { optimisticRemoveComponents } from 'api'
import { useEditorStore } from 'CanvasCore/uiSlice'
import { useUI } from 'Editor'
import React, { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { mutate } from 'swr'
import { pickerGridCards } from 'Editor/lib/visibleComponents'
import BatchUpload from '../BatchUpload'
import { jobMatchesLayer, useCptUploadStore } from '../BatchUpload/uploadQueue'
import { useBatchSelectStore } from './batchSelectStore'
import CoverProgressDialog from './CoverProgressDialog'
import ChangeLayerDialog from './Cpt/ChangeLayer/ChangeLayerDialog'

const COVER_OPTIONS = [
  { value: 0, label: 'No HD Cover', needOriginal: false },
  { value: 1, label: 'HD Cover', needOriginal: true },
  { value: 2, label: 'HD Cover + BG', needOriginal: true },
]

const BatchCoverBar = ({ cpts }) => {
  const { tid } = useParams()
  const lid = useUI((state) => state.lid)
  const deselectAll = useEditorStore((state) => state.deselectCptByLayer)
  const { toast } = useToast()
  const enabled = useBatchSelectStore((s) => s.enabled)
  const selectedIds = useBatchSelectStore((s) => s.selectedIds)
  const setEnabled = useBatchSelectStore((s) => s.setEnabled)
  const selectAll = useBatchSelectStore((s) => s.selectAll)
  const clear = useBatchSelectStore((s) => s.clear)
  const uploadingCount = useCptUploadStore(
    (s) =>
      s.jobs.filter(
        (job) =>
          job.tid === tid &&
          jobMatchesLayer(job, lid) &&
          job.status !== 'error',
      ).length,
  )
  const failedCount = useCptUploadStore(
    (s) =>
      s.jobs.filter(
        (job) =>
          job.tid === tid &&
          jobMatchesLayer(job, lid) &&
          job.status === 'error',
      ).length,
  )
  const retryFailedJobs = useCptUploadStore((s) => s.retryFailedJobs)
  const [progress, setProgress] = useState({
    open: false,
    current: 0,
    total: 0,
    label: '',
  })
  const [layerDialogOpen, setLayerDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const deselectCpt = useEditorStore((state) => state.deselectCpt)

  const cptById = useMemo(() => {
    const map = new Map()
    cpts?.forEach((cpt) => map.set(cpt.id, cpt))
    return map
  }, [cpts])

  const allVisibleIds = useMemo(
    () => pickerGridCards(cpts).map((cpt) => cpt.id),
    [cpts],
  )
  const allSelected =
    allVisibleIds.length > 0 &&
    allVisibleIds.every((id) => selectedIds.includes(id))

  const handleSelectAll = () => {
    if (allSelected) {
      clear()
      return
    }
    selectAll(allVisibleIds)
  }

  const selectedComponents = useMemo(() => {
    return selectedIds
      .map((id) => cptById.get(id))
      .filter(Boolean)
      .filter((cpt) => {
        if (cpt.pid) return false
        const hasChild = cpts?.some((c) => c.pid === cpt.id)
        return !hasChild
      })
  }, [selectedIds, cptById, cpts])

  const apply = async (value, label, needOriginal) => {
    if (progress.open || selectedIds.length === 0) return

    let ids = selectedIds
    let skipped = 0
    if (needOriginal) {
      ids = selectedIds.filter((id) => cptById.get(id)?.original)
      skipped = selectedIds.length - ids.length
    }
    if (ids.length === 0) {
      toast({
        variant: 'destructive',
        title: '无法批量更新',
        description: '选中的组件都没有原图',
      })
      return
    }

    setProgress({ open: true, current: 0, total: ids.length, label })
    let success = 0
    let failed = 0

    try {
      for (let i = 0; i < ids.length; i++) {
        try {
          await putFetcher(`/components/${ids[i]}`, {
            arg: { tid, key: 'autoCover', value },
          })
          success += 1
        } catch {
          failed += 1
        }
        setProgress({
          open: true,
          current: i + 1,
          total: ids.length,
          label,
        })
      }
      await mutate(['/components', { tid }])
      clear()
      toast({
        title: failed > 0 ? '批量 Cover 部分成功' : '批量 Cover 已更新',
        description: [
          `${label}：成功 ${success}`,
          skipped > 0 ? `跳过无原图 ${skipped}` : null,
          failed > 0 ? `失败 ${failed}` : null,
        ]
          .filter(Boolean)
          .join('，'),
        className: failed > 0 ? undefined : 'bg-green-500 text-white',
        variant: failed > 0 ? 'destructive' : undefined,
      })
    } finally {
      setProgress({ open: false, current: 0, total: 0, label: '' })
    }
  }

  const openBatchChangeLayer = () => {
    if (selectedComponents.length === 0) {
      toast({
        variant: 'destructive',
        title: '无法更换图层',
        description:
          '选中的组件都是关联样式子组件或带有关联样式，无法移动图层',
      })
      return
    }
    const skipped = selectedIds.length - selectedComponents.length
    if (skipped > 0) {
      toast({
        title: '部分组件已跳过',
        description: `${skipped} 个关联样式组件无法移动，将处理 ${selectedComponents.length} 个`,
      })
    }
    setLayerDialogOpen(true)
  }

  const handleBatchDelete = async () => {
    if (deleting || selectedIds.length === 0) return
    const ids = [...selectedIds]
    setDeleting(true)
    try {
      await delUrlFetcher('/components/batch', {
        arg: { ids },
      })
      ids.forEach((id) => deselectCpt(tid, id))
      optimisticRemoveComponents(tid, ids)
      clear()
      setDeleteDialogOpen(false)
      toast({
        title: 'Components deleted',
        description: `Deleted ${ids.length} component${
          ids.length > 1 ? 's' : ''
        }`,
        className: 'bg-green-500 text-white',
      })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Batch delete failed',
        description: err instanceof Error ? err.message : String(err),
      })
    } finally {
      setDeleting(false)
    }
  }

  const loading = progress.open || deleting

  return (
    <>
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 border-b bg-background/95 px-2 py-2 backdrop-blur">
        <div className="contents">
          <BatchUpload triggerVariant="button" hideTrigger={enabled} />
        </div>
        {!enabled ? (
          <>
            {uploadingCount > 0 ? (
              <span className="text-xs text-muted-foreground">
                上传中 {uploadingCount}
              </span>
            ) : null}
            {failedCount > 0 ? (
              <Button
                size="sm"
                variant="outline"
                className="text-destructive"
                onClick={() => retryFailedJobs({ tid, lid })}
              >
                重试失败 {failedCount}
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="outline"
              onClick={() => deselectAll(tid, lid)}
            >
              清空已选
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="ml-auto"
              onClick={() => setEnabled(true)}
            >
              多选
            </Button>
          </>
        ) : (
          <>
            <Button
              size="sm"
              variant="outline"
              disabled={loading || allVisibleIds.length === 0}
              onClick={handleSelectAll}
            >
              {allSelected ? '取消全选' : '全选'}
            </Button>
            <span className="text-xs text-muted-foreground">
              已选 {selectedIds.length}/{allVisibleIds.length}
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  disabled={loading || selectedIds.length === 0}
                >
                  {loading ? '处理中...' : '批量更换 Cover'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {COVER_OPTIONS.map((opt) => (
                  <DropdownMenuItem
                    key={opt.value}
                    disabled={loading}
                    onSelect={(e) => {
                      e.preventDefault()
                      apply(opt.value, opt.label, opt.needOriginal)
                    }}
                  >
                    {opt.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              size="sm"
              disabled={loading || selectedIds.length === 0}
              onClick={openBatchChangeLayer}
            >
              Batch Change Layer
            </Button>
            <Button
              size="sm"
              variant="destructive"
              disabled={loading || selectedIds.length === 0}
              onClick={() => setDeleteDialogOpen(true)}
            >
              Batch Delete
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={loading}
              className="ml-auto"
              onClick={() => setEnabled(false)}
            >
              退出多选
            </Button>
          </>
        )}
      </div>
      <CoverProgressDialog
        open={progress.open}
        current={progress.current}
        total={progress.total}
        label={progress.label}
      />
      <ChangeLayerDialog
        open={layerDialogOpen}
        onOpenChange={setLayerDialogOpen}
        components={selectedComponents}
        onSuccess={clear}
      />
      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(next) => {
          if (deleting) return
          setDeleteDialogOpen(next)
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete selected components?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedIds.length} selected
              component{selectedIds.length > 1 ? 's' : ''}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              disabled={deleting}
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleting}
              onClick={handleBatchDelete}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default BatchCoverBar
